import { clusterApiUrl, Connection } from '@solana/web3.js';

const DEFAULT_COMMITMENT = 'confirmed';
/** Padrão do web3.js para `confirmed` é 30s; RPCs HTTP costumam precisar de mais tempo. */
const CONFIRM_TRANSACTION_TIMEOUT_MS = 120_000;
const PUBLIC_FALLBACK_ENDPOINTS = [
    'https://solana-rpc.publicnode.com',
    clusterApiUrl('mainnet-beta')
];
const RPC_TIMEOUT_MS = 9000;

function uniqueEndpoints(endpoints) {
    return [...new Set(endpoints.filter(Boolean).map(endpoint => endpoint.trim()).filter(Boolean))];
}

function configuredFallbackEndpoints() {
    const configuredPrimary = process.env.REACT_APP_SOLANA_RPC_URL;
    const configuredFallbacks = (process.env.REACT_APP_SOLANA_RPC_FALLBACK_URLS || '').split(',');

    if (configuredPrimary) {
        return uniqueEndpoints([
            configuredPrimary,
            ...configuredFallbacks
        ]);
    }

    return uniqueEndpoints(PUBLIC_FALLBACK_ENDPOINTS);
}

function shouldTryNextRpc(error) {
    const text = `${error?.message || ''} ${JSON.stringify(error || {})}`.toLowerCase();
    return text.includes('403')
        || text.includes('429')
        || text.includes('forbidden')
        || text.includes('rate limit')
        || text.includes('tempo limite')
        || text.includes('timeout')
        || text.includes('failed to fetch')
        || text.includes('network');
}

export async function readWithRpcFallback(primaryConnection, reader) {
    const endpoints = configuredFallbackEndpoints();
    const tried = new Set();
    let lastError;

    if (primaryConnection) {
        try {
            return await withTimeout(reader(primaryConnection), RPC_TIMEOUT_MS, 'Tempo limite ao ler dados no RPC principal.');
        } catch (err) {
            lastError = err;
            if (!shouldTryNextRpc(err)) throw err;
            if (primaryConnection.rpcEndpoint) tried.add(primaryConnection.rpcEndpoint);
        }
    }

    for (const endpoint of endpoints) {
        if (tried.has(endpoint)) continue;
        try {
            const connection = new Connection(endpoint, DEFAULT_COMMITMENT);
            return await withTimeout(reader(connection), RPC_TIMEOUT_MS, `Tempo limite ao ler dados no RPC ${endpoint}.`);
        } catch (err) {
            lastError = err;
            if (!shouldTryNextRpc(err)) throw err;
        }
    }

    throw lastError;
}

export function withTimeout(promise, timeoutMs, message) {
    return Promise.race([
        promise,
        new Promise((_, reject) => {
            setTimeout(() => reject(new Error(message || 'Tempo limite ao consultar RPC Solana.')), timeoutMs);
        })
    ]);
}

export function getRpcForbiddenMessage() {
    return 'O RPC da Solana bloqueou a leitura de saldo. Configure um RPC próprio em REACT_APP_SOLANA_RPC_URL, por exemplo Helius, QuickNode ou Alchemy.';
}

/**
 * Conexão para envio/confirmar tx: timeout de confirmação mais alto que o padrão (30s).
 */
export function connectionForTransactions(rpcEndpoint) {
    return new Connection(rpcEndpoint, {
        commitment: DEFAULT_COMMITMENT,
        confirmTransactionInitialTimeout: CONFIRM_TRANSACTION_TIMEOUT_MS
    });
}

function isConfirmTimeoutError(err) {
    const msg = `${err?.message || ''}`.toLowerCase();
    return msg.includes('not confirmed in')
        || msg.includes('transactionexpiredtimeouterror')
        || err?.name === 'TransactionExpiredTimeoutError';
}

const CONFIRM_POLL_MS = 1_000;
const CONFIRM_POLL_ATTEMPTS = 45;
const CONFIRM_RPC_CALL_TIMEOUT_MS = 10_000;

function delay(ms) {
    return new Promise(resolve => {
        setTimeout(resolve, ms);
    });
}

async function readSignatureStatus(connection, signature) {
    const { value } = await withTimeout(
        connection.getSignatureStatuses([signature], { searchTransactionHistory: true }),
        CONFIRM_RPC_CALL_TIMEOUT_MS,
        'Tempo limite ao consultar status da transação na RPC.'
    );
    return value?.[0] || null;
}

function isSignatureSettled(status) {
    if (!status || status.err) return false;
    return status.confirmationStatus === 'processed'
        || status.confirmationStatus === 'confirmed'
        || status.confirmationStatus === 'finalized'
        || (typeof status.confirmations === 'number' && status.confirmations > 0);
}

/**
 * Confirma por polling rápido (não usa confirmTransaction de 120s do web3.js).
 */
export async function confirmTransactionRobust(connection, signature, onProgress) {
    for (let attempt = 0; attempt < CONFIRM_POLL_ATTEMPTS; attempt += 1) {
        if (attempt > 0) await delay(CONFIRM_POLL_MS);

        let status;
        try {
            status = await readSignatureStatus(connection, signature);
        } catch (pollErr) {
            if (attempt === CONFIRM_POLL_ATTEMPTS - 1) throw pollErr;
            continue;
        }

        if (status?.err) {
            throw new Error(
                `A transação falhou on-chain (${signature}). Detalhe: ${JSON.stringify(status.err)}`
            );
        }

        if (isSignatureSettled(status)) {
            return { recoveredAfterTimeout: attempt > 2 };
        }

        if (onProgress) {
            onProgress(Math.round((attempt + 1) * CONFIRM_POLL_MS / 1000));
        }
    }

    try {
        const tx = await withTimeout(
            connection.getTransaction(signature, {
                commitment: 'confirmed',
                maxSupportedTransactionVersion: 0
            }),
            CONFIRM_RPC_CALL_TIMEOUT_MS,
            'Tempo limite ao consultar transação na RPC.'
        );
        if (tx && !tx.meta?.err) {
            return { recoveredAfterTimeout: true };
        }
    } catch {
        /* ignore */
    }

    throw new Error(
        `Não foi possível confirmar a transação a tempo (${signature}). `
            + `Consulte o Explorer: https://solscan.io/tx/${signature} — ela pode ter sido processada mesmo assim.`
    );
}
